import { InferType } from 'yup'
import { EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import * as yup from 'yup'
import { yupErc20WeiRequired, yupEthAddrRequired } from './Enums'
import { ZeroAddress } from 'ethers'
import { ERC20Wei } from './conversions'
// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

export const RewardsPlanRowSchema = yup.object({
  minimumPrincipalEarningInterest: yupErc20WeiRequired
    .label('minimumPrincipalEarningInterest')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'minimumPrincipalEarningInterest',

        description: 'Minimum principal earning interest to be eligible for rewards',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  interestRate: yup
    .number()
    .label('Interest Rate')
    .required()
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'interestRate',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 9.5,
        description:
          'The current annualized interest rate the Borrower will pay on principal earning interest in basis points e.g. 500 = 5.00%',
        stringType: StringTypeEnum.APR,
      })
    ),
  dailyInterestRate: yup
    .number()
    .label('Single Day Interest Rate')
    .default(0.0001)
    .required()
    .meta(
      toFieldMetaData({
        propName: 'dailyInterestRate',
        description:
          'The daily interest rate to be accrued on principal earning interest. This is calculated as (1 + interestRate) ^ 1/365 - 1. Uses 18 as a decimal value, so display value should be divided by 100000000000000000 (1 + 18 zeros). For example, 1000000000000000000 = 1.00',
        stringType: StringTypeEnum.SINGLE_DAY_APR,
      })
    ),
})

export const RewardsPlanSchema = yup.object({
  uuid: yup
    .string()
    .required()
    .default('')
    .meta(toFieldMetaData({ propName: 'uuid', description: 'UUID for the rewards plan row' })),
  poolAddr: yupEthAddrRequired
    .label('Vault Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'poolAddr',

        description: 'The smart-contract address of the loan vault. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),

  accountAddr: yupEthAddrRequired
    .label('Account Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'accountAddr',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3,
        description: 'The white-listed blockchain wallet address associated with this account. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),

  rewardsPlanRows: yup
    .array()
    .of(RewardsPlanRowSchema)
    .required()
    .default([])
    .meta(
      toFieldMetaData({
        propName: 'rewardsPlanRows',
        description: 'Rewards plan rows',
      })
    ),

  timestamp: yup
    .number()
    .label('Time Stamp')
    .required()
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'timestamp',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_DATE,
      })
    ),

  currencyLabel: yup
    .string()
    .required()
    .default(' ')
    .label('Label for Liquidity Token')
    .meta(
      toFieldMetaData({
        propName: 'currencyLabel',
        description: 'Label for the Liquidity Token',
        example: 'USDC',
      })
    ),
  poolDisplayName: yup
    .string()
    .required()
    .default(' ')
    .label('Label for Pool Display Name')
    .meta(toFieldMetaData({ propName: 'poolDisplayName', description: 'Label for the Pool Display Name' })),
  companyName: yup
    .string()
    .required()
    .default(' ')
    .label('Label for Company Name')
    .meta(toFieldMetaData({ propName: 'companyName', description: 'Label for the Company Name' })),
  poolAddrLenderAddr: yup
    .string()
    .required()
    .default(' ')
    .label('Compound Key')
    .meta(toFieldMetaData({ propName: 'poolAddrLenderAddr', description: 'Compound Key for the Rewards Plan' })),

  startDateString: yup
    .string()
    .required()
    .default('')
    .label('Start Date String')
    .meta(toFieldMetaData({ propName: 'startDateString', description: 'Start Date String for the Rewards Plan' })),
  endDateString: yup
    .string()
    .required()
    .default('')
    .label('End Date String')
    .meta(toFieldMetaData({ propName: 'endDateString', description: 'End Date String for the Rewards Plan' })),
})

export interface RewardsPlanRowCTO {
  minimumPrincipalEarningInterest: ERC20Wei
  interestRate: number
  dailyInterestRate: number
}
type RewardPlanCTORowInfer = InferType<typeof RewardsPlanRowSchema>
export const _testARow: RewardsPlanRowCTO = {} as unknown as RewardPlanCTORowInfer
export const _testBRow: RewardPlanCTORowInfer = {} as unknown as RewardsPlanRowCTO

export interface RewardsPlanCTO {
  uuid: string
  poolAddr: EthAddr
  accountAddr: EthAddr
  poolAddrLenderAddr: string
  rewardsPlanRows: RewardsPlanRowCTO[]
  timestamp: number
  currencyLabel: string
  poolDisplayName: string
  companyName: string
  startDateString: string
  endDateString: string
}
type RewardsPlanCTOInfer = InferType<typeof RewardsPlanSchema>
export const _testA: RewardsPlanCTO = {} as unknown as RewardsPlanCTOInfer
export const _testB: RewardsPlanCTOInfer = {} as unknown as RewardsPlanCTO

export enum RewardsPlanCTOActionEnum {
  UNDEFINED = 'UNDEFINED',
  SAVE = 'SAVE',
}
export interface RewardsPlanCTOAction {
  rewardsPlan?: RewardsPlanCTO
  poolAddr?: EthAddr
  accountAddr?: EthAddr
  action: RewardsPlanCTOActionEnum
}
